import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Alert,
  Button,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { formatDateDisplay } from '../../utils/dateUtils';
import { timeOffRequestService } from '../../services/timeOffRequestService';
import { Link as RouterLink } from 'react-router-dom';

const displayValue = (v) => (v == null || v === '' ? '—' : String(v));

const formatRange = (row) => {
  const start = row.start_date ? formatDateDisplay(row.start_date) : '—';
  const end = row.end_date ? formatDateDisplay(row.end_date) : '—';
  if (row.request_mode === 'partial_day') {
    const hrs = row.hours != null ? `${Number(row.hours).toFixed(2)} hrs` : '';
    return `${start}${hrs ? ` · ${hrs}` : ''}`;
  }
  if (start === end) return start;
  return `${start} – ${end}`;
};

const EmployeeProfile = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [timeOffDecisions, setTimeOffDecisions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await timeOffRequestService.listUnseenDecisions();
        if (!cancelled) setTimeOffDecisions(res.data || []);
      } catch {
        /* optional banner */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismissTimeOff = async () => {
    try {
      await timeOffRequestService.markDecisionsSeen();
      setTimeOffDecisions([]);
    } catch {
      /* ignore */
    }
  };

  const handleChangePassword = async () => {
    setMessage('');
    setError('');
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setMessage('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1.5 }}>
        Profile
      </Typography>

      {timeOffDecisions.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleDismissTimeOff}>
              Dismiss
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Time off updates from management
          </Typography>
          {timeOffDecisions.map((row) => {
            const reviewer = [row.reviewed_by_first_name, row.reviewed_by_last_name]
              .filter(Boolean)
              .join(' ');
            return (
              <Typography key={row.id} variant="body2">
                Your request for <strong>{formatRange(row)}</strong> was <strong>{row.status}</strong>
                {reviewer ? ` by ${reviewer}` : ''}.
              </Typography>
            );
          })}
          <Button
            component={RouterLink}
            to="/employee/time-off"
            size="small"
            sx={{ mt: 0.5, textTransform: 'none' }}
          >
            View time off requests
          </Button>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.25}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="First Name" value={displayValue(user?.first_name)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Last Name" value={displayValue(user?.last_name)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Email" value={displayValue(user?.email)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Phone" value={displayValue(user?.phone)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Date of Hire"
              value={user?.date_of_hire ? formatDateDisplay(user.date_of_hire) : '—'}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Department" value={displayValue(user?.department)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Job Title" value={displayValue(user?.job_title)} InputProps={{ readOnly: true }} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Change password</Typography>
        {message && <Alert severity="success" sx={{ mb: 1.5 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Grid container spacing={1.25}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" disabled={saving} onClick={handleChangePassword}>
              {saving ? 'Saving…' : 'Update password'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default EmployeeProfile;
